/**
 * Bug / Feature Report Service
 *
 * CRUD for user-submitted bug reports and feature requests.
 * Stored in Firestore `bug_reports` collection.
 */

import { db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, addDoc, updateDoc, doc, query, where, orderBy, onSnapshot, arrayUnion } from 'firebase/firestore';
import { Mention, MentionEntityType, MentionStatus } from '@/types/mention.types';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import {
  bugReportDoneNotice,
  bugReportPath,
  bugReportStatusPatch,
  shouldNotifyBugReporter,
  type BugReportStatus,
  type BugReportType,
} from '@shared/domain/bug-reports';
import {
  autobuildAnswerWrite,
  type AutobuildDecision,
  type AutobuildRedirect,
  type AutobuildSource,
  type AutobuildState,
  type AutobuildThreadPost,
  type AutobuildVerdict,
} from '@shared/domain/autobuild';
import type { UsageBreadcrumb } from '@shared/domain/usage';

// The Claude connector reads and updates these same docs — status rules and
// the done-notification copy live in shared/ so the two can't drift.
export type ReportType = BugReportType;
export type ReportStatus = BugReportStatus;

export interface BugReport {
  id?: string;
  tenantId: string;
  type: ReportType;
  title: string;
  description: string;
  page: string;           // URL path where the report was submitted
  reportedBy: string;     // userId
  reportedByName: string; // userName
  reportedByRole: string; // user role
  status: ReportStatus;
  adminNotes?: string;
  photoUrls?: string[];   // Screenshot/photo URLs from Firebase Storage

  // Resolution info — captured when the report is marked 'done'
  resolutionMessage?: string;  // Blurb from the fixer about what they did
  resolvedAt?: string;         // ISO timestamp
  resolvedBy?: string;         // userId
  resolvedByName?: string;     // userName

  /**
   * The autobuild pipeline's own state (shared/src/domain/autobuild.ts). Only
   * the pipeline, the owner-verified callables (`autobuildDecide`,
   * `autobuildRedirect`), the thread callable (`autobuildThreadPost`) and the
   * reporter's answer (answerAutobuildQuestion) write it; `decision`,
   * `redirect`, `thread`, `scope` and `slack` are pinned server-side.
   */
  autobuild?: AutobuildState;

  /**
   * The last events of the reporter's session when they lodged it (screens,
   * clicks by component, errors, empty results — never a value), and the
   * normalised route they were on. From the usage capture (src/lib/usage);
   * the pipeline's understand phase reads them first.
   */
  breadcrumbs?: UsageBreadcrumb[];
  routePattern?: string;

  createdAt: string;
  updatedAt: string;
}

/**
 * Submit a new bug report or feature request.
 */
export async function submitBugReport(report: Omit<BugReport, 'id' | 'status' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const now = new Date().toISOString();
  const docRef = await addDoc(collection(db, 'bug_reports'), {
    ...report,
    status: 'new' as ReportStatus,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

/**
 * Strip undefined values from an object before writing to Firestore.
 * Firestore rejects documents containing undefined.
 */
function sanitize<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as T;
}

/**
 * Update a bug report (admin actions: status change, notes).
 */
export async function updateBugReport(id: string, updates: Partial<BugReport>): Promise<void> {
  await updateDoc(doc(db, 'bug_reports', id), sanitize({
    ...updates,
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Notify the reporter that their bug/feature report has been completed.
 * Creates a direct mention notification (shows up in their bell).
 * Fire-and-forget — never blocks the status update.
 *
 * If `resolutionMessage` is provided, the fixer's custom blurb is used as
 * the notification body so the reporter can see what was actually done.
 */
export async function notifyBugReportDone(params: {
  report: BugReport;
  completedBy: string;
  completedByName: string;
  resolutionMessage?: string;
}): Promise<string | null> {
  const { report, completedBy, completedByName, resolutionMessage } = params;

  // No reporter, or they're ticking off their own report — nobody to tell.
  if (!report.id || !shouldNotifyBugReporter(report.reportedBy, completedBy)) {
    return null;
  }

  const { entityTitle, noteContent, noteExcerpt } = bugReportDoneNotice(report, resolutionMessage);

  const mentionDoc: Omit<Mention, 'id'> = {
    tenantId: report.tenantId,
    mentionedUserId: report.reportedBy,
    mentionedUserName: report.reportedByName,
    createdBy: completedBy,
    createdByName: completedByName,
    entityType: MentionEntityType.BUG_FIX,
    entityId: report.id,
    entityTitle,
    noteContent,
    noteExcerpt,
    status: MentionStatus.UNREAD,
    createdAt: new Date().toISOString(),
    linkPath: bugReportPath(report.id),
  };

  try {
    const docRef = await addDoc(collection(db, 'mentions'), mentionDoc);
    return docRef.id;
  } catch (err) {
    console.error('[BugReports] Failed to notify reporter:', err);
    return null;
  }
}

/**
 * The reporter (or a director on their behalf) answering the one question
 * the pipeline parked the report on. Writes the shared answer patch, logs it
 * in the answerer's name, and puts the report back on 'new' — that reopen is
 * what the 5-minute scheduler resumes the build on.
 */
export async function answerAutobuildQuestion(
  report: BugReport,
  answer: string,
  by: { id: string; name: string },
): Promise<void> {
  if (!report.id) throw new Error('Report has no id');
  const trimmed = answer.trim();
  if (!trimmed) throw new Error('Answer is empty');
  const write = autobuildAnswerWrite({ answer: trimmed, byId: by.id, byName: by.name });
  await updateDoc(doc(db, 'bug_reports', report.id), {
    ...write.fields,
    'autobuild.log': arrayUnion(write.logEntry),
    ...bugReportStatusPatch(report.status, { to: 'new' }),
    updatedAt: write.logEntry.at,
  });
}

/**
 * A director's Approve / Request changes on a build parked on
 * awaitingApproval. Goes through the `autobuildDecide` callable, which checks
 * the caller is in tenant.ownerIds and writes the decision with the Admin
 * SDK — the client never writes `autobuild.decision` itself.
 */
export async function decideAutobuild(
  reportId: string,
  verdict: AutobuildVerdict,
  note: string,
  via: Extract<AutobuildSource, 'theo' | 'plate'>,
): Promise<AutobuildDecision> {
  const call = httpsCallable<
    { reportId: string; verdict: AutobuildVerdict; note: string; via: AutobuildSource },
    { ok: true; decision: AutobuildDecision }
  >(functions, 'autobuildDecide');
  const result = await call({ reportId, verdict, note: note.trim(), via });
  return result.data.decision;
}

/**
 * A director's Redirect on a build in flight — "you're building the wrong
 * thing". Goes through the `autobuildRedirect` callable (owner check, the run
 * in flight is stopped, the branch and PR kept); the dispatcher then starts a
 * fresh understand phase whose spec begins with the note. The client never
 * writes `autobuild.redirect` itself.
 */
export async function redirectAutobuild(
  reportId: string,
  note: string,
  via: Extract<AutobuildSource, 'theo' | 'plate'>,
): Promise<{ redirect: AutobuildRedirect; cancelled: string }> {
  const call = httpsCallable<
    { reportId: string; note: string; via: AutobuildSource },
    { ok: true; redirect: AutobuildRedirect; cancelled: string }
  >(functions, 'autobuildRedirect');
  const result = await call({ reportId, note: note.trim(), via });
  return { redirect: result.data.redirect, cancelled: result.data.cancelled };
}

/**
 * A post in the report's Autobuild thread — anyone with access can write;
 * a director's post counts as an instruction for the build. Through the
 * `autobuildThreadPost` callable (the writer's name comes from their users
 * doc, the post is mirrored to #theo-builds, and while the pipeline is
 * waiting on a question the post is the answer).
 */
export async function postAutobuildThread(
  reportId: string,
  text: string,
  via: Extract<AutobuildSource, 'theo' | 'plate'>,
): Promise<{ post: AutobuildThreadPost; answered: boolean }> {
  const call = httpsCallable<
    { reportId: string; text: string; via: AutobuildSource },
    { ok: true; post: AutobuildThreadPost; answered: boolean }
  >(functions, 'autobuildThreadPost');
  const result = await call({ reportId, text: text.trim(), via });
  return { post: result.data.post, answered: result.data.answered };
}

/**
 * Subscribe to ONE report (real-time). A single-doc listen is not subject to
 * the list-query tenant rule, so no tenant filter is needed here.
 */
export function subscribeBugReport(
  reportId: string,
  callback: (report: BugReport | null) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'bug_reports', reportId),
    (snapshot) => {
      callback(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as BugReport) : null);
    },
    (err) => {
      console.error('[BugReports] Report subscription error:', err);
      onError?.(err);
    },
  );
}

/**
 * Subscribe to all bug reports (real-time).
 * Tenant-filtered so a future strict isolation rule won't reject this listen
 * query — requires composite index (tenantId, createdAt).
 */
export function subscribeBugReports(
  callback: (reports: BugReport[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, 'bug_reports'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    orderBy('createdAt', 'desc'),
  );

  return onSnapshot(q, (snapshot) => {
    const reports = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as BugReport[];
    callback(reports);
  }, (err) => {
    console.error('[BugReports] Subscription error:', err);
    onError?.(err);
  });
}
